import React, { useEffect, useMemo, useState } from 'react';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { IMarineHullDetailsDrawerProps } from '.';
import { getMarineHullSections } from './content';
import { useMutation } from '@apollo/client';
import { getError } from '@utils/graph-utils';
import { useParams } from 'react-router-dom';
import { updateMarineHull } from './queries';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';

const MarineHullDetailsDrawer: React.FC<IMarineHullDetailsDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  data,
}) => {
  const params = useParams();
  const [initialized, setInitialized] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [marineHullAction] = useMutation(updateMarineHull());

  useEffect(() => {
    setInitialized(true);
  }, []);

  const sections = useMemo(() => {
    return getMarineHullSections(data);
  }, [data]);

  const handleSubmit = async (values: Record<string, any>) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const policyMarineHullInput = {
        vesselName: values.vesselName,
        connectionPort: values.connectionPort,
        territorialWaters: values.territorialWaters,
        usage: values.usage,
        builder: values.builder,
        constructionYear: values.constructionYear,
        registeryPort: values.registryPort,
        length: values.length,
        weight: values.weight,
        height: values.height,
        engineType: values.engineType,
        engineNumber: values.engineNumber,
        engineYearBuilt: values.engineYearBuilt,
        engineHorsePower: values.engineHorsePower,
        engineMaxDesignedSpeed: values.engineMaxDesignedSpeed,
        engineFuel: values.engineFuel,
        remarks: values.remarks,
      };

      const { errors } = await marineHullAction({
        variables: {
          entityId: params.id,
          policyMarineHullInput,
        },
      });

      if (!errors || errors.length === 0) {
        toast.success(
          <ToastSuccessMessage>
            Marine Hull details updated successfully!
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (error) {
      const errorMessage = getError({ error });
      toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      isOpen={open}
      onClose={onClose}
      title="Modify Marine Hull Details"
      loading={!initialized}
      sections={sections}
      onSubmit={handleSubmit}
      submitButtonState={submitButtonState}
      isSubmitButtonDisabled={!!submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default MarineHullDetailsDrawer;
